import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, RefreshCw, Loader2 } from "lucide-react";
import { api, BACKEND_URL, formatApiError } from "../../lib/api";

const src = (p) => (p.startsWith("http") ? p : `${BACKEND_URL}/api/files/${p.replace(/^\/api\/files\//, "")}`);
const LABEL = { review: "Waiting for you", pending: "Queued", painting: "Painting photo", filming: "Making video", retrying: "Retrying", ready: "Live", failed: "Failed" };

function ReviewCard({ it, reload }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const id = `${it.target}-${it.key.replace(".", "-")}`;
  const send = (approve) => {
    setBusy(true);
    api.post("/admin/showcase/review", { target: it.target, key: it.key, approve, note })
      .then(() => { toast.success(approve ? "Approved. Making the video now" : "Repainting with your note"); setNote(""); reload(); })
      .catch((e) => toast.error(formatApiError(e))).finally(() => setBusy(false));
  };
  const working = ["painting", "filming", "pending", "retrying"].includes(it.status);
  return (
    <div className="border border-[var(--line)] rounded-2xl overflow-hidden bg-white" data-testid={`review-card-${id}`}>
      <div className="relative aspect-video bg-[var(--bg-warm)]">
        {it.image ? <img src={src(it.image)} alt={it.title} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-xs text-[var(--muted)] gap-2">{working && <Loader2 className="w-4 h-4 animate-spin" />}No photo yet</div>}
        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${it.status === "review" ? "bg-[var(--gold)] text-white" : "bg-black/60 text-white"}`} data-testid={`review-status-${id}`}>{LABEL[it.status] || it.status}</span>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-sm font-medium">{it.title}</p>
        {it.note && <p className="text-[11px] text-[var(--muted)]">Last note: {it.note}</p>}
        {!["painting", "filming"].includes(it.status) && <>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400} placeholder="What should the AI fix? e.g. the phone is held backwards" className="input-luxe w-full text-sm" data-testid={`review-note-${id}`} />
          <div className="flex flex-wrap gap-2">
            {it.status === "review" && <button disabled={busy} onClick={() => send(true)} className="btn-gold !py-2 inline-flex items-center gap-1 text-sm" data-testid={`review-approve-${id}`}><Check className="w-4 h-4" />Approve</button>}
            <button disabled={busy || !note.trim()} onClick={() => send(false)} className="btn-outline-gold !py-2 inline-flex items-center gap-1 text-sm disabled:opacity-40" data-testid={`review-reject-${id}`}><RefreshCw className="w-4 h-4" />{it.status === "ready" ? "Redo with note" : "Reject & repaint"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}

export function SceneReview() {
  const [items, setItems] = useState([]);
  const [all, setAll] = useState(false);
  const load = useCallback(() => api.get("/admin/showcase/review").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const waiting = items.filter((x) => x.status === "review");
  const list = all ? items : waiting;
  return (
    <div className="border-t border-[var(--line)] pt-4 mt-4" data-testid="scene-review">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="font-serif text-xl">AI photo approval <span className="text-sm text-[var(--muted)]" data-testid="scene-review-count">({waiting.length} waiting)</span></p>
        <button onClick={() => setAll((v) => !v)} className="text-xs underline text-[var(--gold)]" data-testid="scene-review-toggle-all">{all ? "Show only waiting" : "Show all scenes"}</button>
      </div>
      <p className="text-xs text-[var(--muted)] mb-3">No video is made until you approve its photo. Reject with a note and the AI repaints it.</p>
      {list.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{list.map((it) => <ReviewCard key={`${it.target}-${it.key}`} it={it} reload={load} />)}</div>
        : <p className="text-sm text-[var(--muted)]" data-testid="scene-review-empty">Nothing waiting for approval.</p>}
    </div>
  );
}
